import React from 'react';
import { observer } from 'mobx-react-lite';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { ProgressSpinner } from 'primereact/progressspinner';
import { rootStore } from 'stores/RootStore';
import styles from './ticketDetailModal.module.scss';

export const TicketDetailModal: React.FC = observer(() => {
  const { SupportTicketStore } = rootStore;
  const { selectedTicket, isTicketDetailLoading, isSyncingStatus } = SupportTicketStore;

  const closeDetailModal = () => {
    SupportTicketStore.setDetailModalVisible(false);
  };

  const handleSyncStatus = async () => {
    if (selectedTicket) {
      await SupportTicketStore.syncTicketStatus(selectedTicket.jiraTicketId);
    }
  };

  const getStatusSeverity = (status: string): 'success' | 'info' | 'warning' | 'danger' => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('done') || statusLower.includes('resolved')) return 'success';
    if (statusLower.includes('progress')) return 'info';
    if (statusLower.includes('review')) return 'warning';
    return 'danger';
  };

  const getEnvironmentSeverity = (
    env: string
  ): 'success' | 'info' | 'warning' | 'danger' | undefined => {
    if (env === 'production') return 'danger';
    if (env === 'staging') return 'warning';
    if (env === 'dev') return 'info';
    return undefined;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const headerContent = (
    <div className={styles.headerContent}>
      <span>Ticket Details</span>
      {selectedTicket && (
        <Button
          icon="pi pi-refresh"
          label="Sync Status"
          onClick={handleSyncStatus}
          loading={isSyncingStatus}
          size="small"
          outlined
          className={styles.syncButton}
        />
      )}
    </div>
  );

  return (
    <Dialog
      header={headerContent}
      visible={SupportTicketStore.isDetailModalVisible}
      onHide={closeDetailModal}
      style={{ width: '700px' }}
      draggable={false}
      resizable={false}
      blockScroll
      className={styles.ticketDetailDialog}
    >
      {isTicketDetailLoading ? (
        <div className={styles.loadingContainer}>
          <ProgressSpinner />
          <p>Loading ticket details...</p>
        </div>
      ) : selectedTicket ? (
        <div className={styles.modalContainer}>
          {/* Ticket ID and Status */}
          <div className={styles.headerSection}>
            <div className={styles.ticketIdSection}>
              <span className={styles.ticketIdLabel}>Ticket ID:</span>
              <a
                href={selectedTicket.jiraUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.ticketId}
              >
                {selectedTicket.jiraTicketId}
                <i className="pi pi-external-link" style={{ marginLeft: '0.5rem' }}></i>
              </a>
            </div>
            <Tag value={selectedTicket.status} severity={getStatusSeverity(selectedTicket.status)} />
          </div>

          <Divider />

          {/* Summary */}
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}>Summary</h4>
            <p className={styles.summary}>{selectedTicket.summary}</p>
          </div>

          {/* Description */}
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}>Description</h4>
            <p className={styles.description}>{selectedTicket.description}</p>
          </div>

          <Divider />

          {/* Details Grid */}
          <div className={styles.detailsGrid}>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Category:</span>
              <Tag value={selectedTicket.category} />
            </div>

            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Environment:</span>
              <Tag
                value={selectedTicket.environment}
                severity={getEnvironmentSeverity(selectedTicket.environment)}
              />
            </div>

            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Metadata:</span>
              <code className={styles.codeText}>{selectedTicket.pageRoute}</code>
            </div>

            {selectedTicket.reporterEmail && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Reporter:</span>
                <span>{selectedTicket.reporterEmail}</span>
              </div>
            )}

            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Created:</span>
              <span>{formatDate(selectedTicket.createdAt)}</span>
            </div>

            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Last Updated:</span>
              <span>{formatDate(selectedTicket.updatedAt)}</span>
            </div>
          </div>

          {/* Comments */}
          {selectedTicket.comments && selectedTicket.comments.length > 0 && (
            <>
              <Divider />
              <div className={styles.section}>
                <h4 className={styles.sectionTitle}>
                  Comments ({selectedTicket.comments.length})
                </h4>
                <div className={styles.commentsList}>
                  {selectedTicket.comments.map((comment) => (
                    <div key={comment.id} className={styles.commentItem}>
                      <div className={styles.commentHeader}>
                        <span className={styles.commentAuthor}>
                          <i className="pi pi-user" style={{ marginRight: '0.5rem' }}></i>
                          {comment.author}
                        </span>
                        <span className={styles.commentDate}>
                          {formatDate(comment.created)}
                        </span>
                      </div>
                      <p className={styles.commentBody}>{comment.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Close Button */}
          <div className={styles.buttonWrapper}>
            <Button label="Close" icon="pi pi-times" onClick={closeDetailModal} outlined />
          </div>
        </div>
      ) : (
        <div className={styles.errorContainer}>
          <i className="pi pi-exclamation-circle" style={{ fontSize: '3rem', color: '#d8000c' }}></i>
          <p>Failed to load ticket details</p>
        </div>
      )}
    </Dialog>
  );
});
